import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  OutputNodeDefinition,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { validateJoinKeyColumnTypes } from '../utils'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class Join extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.join',
        group: 'Joins',
        title: 'Join',
        description: `
      This component combines rows from two separate tables based on a related column between them.
      `,
        starred: true
      },
      [
        {
          name: 'maintable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'secondarytable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Secondary table'
        },
        {
          name: 'maincolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Column in main table',
          parent: 'maintable',
          dataType: [
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp,
            ColumnDataType.Number,
            ColumnDataType.String
          ]
        },
        {
          name: 'secondarycolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Column in secondary table',
          parent: 'secondarytable',
          dataType: [
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp,
            ColumnDataType.Number,
            ColumnDataType.String
          ]
        },
        {
          name: 'jointype',
          type: WorkflowComponentParamType.Selection,
          title: 'Join type',
          options: ['Inner', 'Left', 'Right', 'Full outer'],
          default: 'Inner'
        }
      ]
    )
    // This component is hidden from the UI but is still available for backwards compatibility.
    // The joinv2 component is the one that is used.
    this.hidden = true
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const prepParams = this._prepareParameters(params)
    let colString = ''
    const columns = schemas?.input?.['secondarytable'] as TableSchema
    if (columns) {
      colString = columns
        .map((x) => {
          if (connection.type === Provider.Oracle) {
            return `"_joined".${x.name} AS ${x.name}_joined`
          }
          return `_joined.${x.name} AS ${x.name}_joined`
        })
        .join(', ')
    } else {
      return {
        query: '',
        outputs: {}
      }
    }
    const select =
      connection.type === Provider.Oracle
        ? dedent`
        SELECT
          "_main".*,
          ${colString}
        FROM
          ${connection.quoteName(prepParams.maintable as string)} "_main"
        ${(prepParams.jointype as string).toUpperCase()} JOIN
          ${connection.quoteName(prepParams.secondarytable as string)} "_joined"
        ON
          "_main".${prepParams.maincolumn} = "_joined".${prepParams.secondarycolumn}
      `
        : dedent`
        SELECT ${connection.type === Provider.DatabricksRest ? '/*+ REPARTITION(40) */' : ''}
          _main.*,
          ${colString}
        FROM
          ${connection.quoteName(prepParams.maintable as string)} AS _main
        ${(prepParams.jointype as string).toUpperCase()} JOIN
          ${connection.quoteName(prepParams.secondarytable as string)} AS _joined
        ON
          _main.${prepParams.maincolumn} = _joined.${prepParams.secondarycolumn}
      `
    let createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    if (connection.type === Provider.DatabricksRest) {
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)
    }
    return {
      query: createTable,
      outputs: { result: outputTablename }
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    const keyTypeError = validateJoinKeyColumnTypes(
      schemas,
      params.maintable as WorkflowComponentValue,
      params.secondarytable as WorkflowComponentValue,
      resolvedParamValues.maincolumn as string,
      resolvedParamValues.secondarycolumn as string,
      'tables'
    )
    if (keyTypeError) {
      problems.push(keyTypeError)
    }
    return problems
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  getVersion(provider: Provider) {
    return '1.2'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    const schema = columns.maintable.map((column) => {
      return {
        name: `${column.name}`,
        type: column.type
      }
    })
    columns.secondarytable.forEach((column) => {
      schema.push({
        name: `${column.name}_joined`,
        type: column.type
      })
    })
    return schema
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { Join }
